import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { ESTADOS_MX } from './Portal';

interface EnRed {
  proveedorId: string;
  nombreComercial: string;
  ciudad: string;
  estadoMx: string;
  especialidades: string[];
  calificacion: number | null;
  trabajosTerminados: number;
}
export interface EntradaDirectorio {
  id: string;
  nombre: string;
  especialidades: string[];
  telefono: string | null;
  ciudad: string | null;
  proveedorId: string | null;
  deConfianza: boolean;
}
interface Especialidad {
  clave: string;
  nombre: string;
}

/** Directorio del lotero: proveedores de confianza de la red y los que no usan la plataforma. */
export function Directorio() {
  const api = useApi();
  const directorio = useConsulta<EntradaDirectorio[]>('/workshop/proveedores/directorio');
  const { datos: especialidades } = useConsulta<Especialidad[]>('/workshop/especialidades');
  const [filtro, setFiltro] = useState({ especialidad: '', estadoMx: '' });
  const consulta = new URLSearchParams(Object.entries(filtro).filter(([, v]) => v)).toString();
  const red = useConsulta<EnRed[]>(`/workshop/proveedores/red${consulta ? `?${consulta}` : ''}`);
  const [error, setError] = useState<string | null>(null);
  const nombreEsp = (c: string) => especialidades?.find((e) => e.clave === c)?.nombre ?? c;
  const enDirectorio = new Map(
    (directorio.datos ?? []).filter((d) => d.proveedorId).map((d) => [d.proveedorId, d]),
  );

  const guardar = async (cuerpo: Record<string, unknown>, id?: string) => {
    setError(null);
    try {
      await api(
        id ? 'PUT' : 'POST',
        `/workshop/proveedores/directorio${id ? `/${id}` : ''}`,
        cuerpo,
      );
      directorio.recargar();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const agregarExterno = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const forma = e.currentTarget;
    const f = new FormData(forma);
    void guardar({
      nombre: f.get('nombre'),
      ...(f.get('telefono') && { telefono: f.get('telefono') }),
      ...(f.get('ciudad') && { ciudad: f.get('ciudad') }),
      especialidades: f.getAll('especialidad').map(String).filter(Boolean),
    }).then(() => forma.reset());
  };
  return (
    <>
      <div className="encabezado">
        <div>
          <div className="subtitulo">
            <Link to="/proveedores">Proveedores</Link> ›
          </div>
          <h1>Directorio de proveedores</h1>
          <div className="subtitulo">
            Tus proveedores de confianza reciben primero tus solicitudes
          </div>
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      <section className="seccion">
        <header>
          <h2>Mi directorio</h2>
        </header>
        {directorio.datos && !directorio.datos.length ? (
          <p className="vacio">Vacío. Agrega proveedores de la red o los que conoces por fuera.</p>
        ) : (
          <table>
            <tbody>
              {(directorio.datos ?? []).map((d) => (
                <tr key={d.id}>
                  <td>
                    {d.nombre}
                    <div className="vin">{d.especialidades.map(nombreEsp).join(', ') || '—'}</div>
                  </td>
                  <td>
                    {d.proveedorId ? (
                      <span className="etiqueta exito">en la red</span>
                    ) : (
                      <span className="etiqueta">externo · {d.telefono ?? 'sin teléfono'}</span>
                    )}
                  </td>
                  <td className="num">
                    {d.proveedorId && (
                      <button
                        className="boton"
                        onClick={() =>
                          void guardar(
                            {
                              nombre: d.nombre,
                              especialidades: d.especialidades,
                              proveedorId: d.proveedorId,
                              deConfianza: !d.deConfianza,
                            },
                            d.id,
                          )
                        }
                      >
                        {d.deConfianza ? '★ De confianza' : 'Marcar de confianza'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <form
          className="cuerpo formulario"
          onSubmit={agregarExterno}
          style={{ borderTop: '1px solid var(--borde-suave)' }}
        >
          <label className="campo">
            <span>Proveedor externo (sin cuenta)</span>
            <input name="nombre" required maxLength={120} placeholder="Don Chuy, hojalatero" />
          </label>
          <label className="campo">
            <span>Teléfono</span>
            <input name="telefono" type="tel" maxLength={30} />
          </label>
          <label className="campo">
            <span>Ciudad</span>
            <input name="ciudad" maxLength={80} />
          </label>
          <label className="campo">
            <span>Especialidad</span>
            <select name="especialidad" defaultValue="">
              <option value="">—</option>
              {(especialidades ?? []).map((e) => (
                <option key={e.clave} value={e.clave}>
                  {e.nombre}
                </option>
              ))}
            </select>
          </label>
          <div className="acciones">
            <button className="boton">Agregar externo</button>
          </div>
        </form>
      </section>
      <section className="seccion">
        <header>
          <h2>Red de proveedores verificados</h2>
          <div style={{ flex: 1 }} />
          <select
            value={filtro.especialidad}
            onChange={(e) => setFiltro((f) => ({ ...f, especialidad: e.target.value }))}
          >
            <option value="">Todas las especialidades</option>
            {(especialidades ?? []).map((e) => (
              <option key={e.clave} value={e.clave}>
                {e.nombre}
              </option>
            ))}
          </select>
          <select
            value={filtro.estadoMx}
            onChange={(e) => setFiltro((f) => ({ ...f, estadoMx: e.target.value }))}
          >
            <option value="">Todo México</option>
            {ESTADOS_MX.map(([c, n]) => (
              <option key={c} value={c}>
                {n}
              </option>
            ))}
          </select>
        </header>
        {red.datos && !red.datos.length ? (
          <p className="vacio">No hay proveedores verificados con ese filtro.</p>
        ) : (
          <table>
            <tbody>
              {(red.datos ?? []).map((p) => (
                <tr key={p.proveedorId}>
                  <td>
                    {p.nombreComercial}
                    <div className="vin">{p.especialidades.map(nombreEsp).join(', ')}</div>
                  </td>
                  <td>
                    {p.ciudad}, {p.estadoMx}
                  </td>
                  <td>
                    {p.calificacion
                      ? `${p.calificacion} ★ · ${p.trabajosTerminados} trabajos`
                      : 'Sin calificaciones'}
                  </td>
                  <td className="num">
                    {enDirectorio.has(p.proveedorId) ? (
                      <span className="etiqueta exito">en tu directorio</span>
                    ) : (
                      <button
                        className="boton"
                        onClick={() =>
                          void guardar({
                            nombre: p.nombreComercial,
                            especialidades: p.especialidades,
                            proveedorId: p.proveedorId,
                            deConfianza: true,
                          })
                        }
                      >
                        Agregar de confianza
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
